import React, { useState } from 'react'
import { Select, Tooltip, Button, Avatar } from 'antd'
import { cloneDeep, set, sortBy } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { Organization } from '../../reducers/organizationReducer'
import { useAxiosWithHeader } from '../../utils'
import CreatableSelect from 'react-select/creatable'
import { components as selectComponents } from 'react-select'
import { Engineer } from '../../reducers/engineerReducer'
import messageInteraction from '../Analytics/utils/messageInteraction'
import { handleErrorsInputTeamChannel } from './helpers'
import { dedupeByStableId } from './selectorUtils'
import {
  classActionColumn,
  classChannelColumn,
  classCreatorColumn,
  classOptionTeamChannel,
  classTeamColumn,
} from '../EngineerTeamChannelContainer/teamChannelTableHelper'
import RefreshTeamButton from '../utils/refreshTeamsButton'
import RefreshConversationsButton from '../utils/refreshConversationsButton'
import { useEffectOnce } from 'react-use'
import { PlusCircleIcon } from '@heroicons/react/outline'
import { UserAvatar } from '../../pages/OpenChannels/userAvatar'
import { ChannelIdInputOption } from './ChannelIdInput'

const slugify = require('slugify')

const initialState = {
  title: '',
  titleStatus: '',
  existingChannel: false,
  teams: [],
  teamStatus: '',
  notificationTrigger: 'team_membership',
}

export default function TeamChannelInputTable({
  setLoading,
  loading,
  children,
  preFillAxoloGeneral,
  allText,
  setHasCreatedChannel,
  onboarding,
}) {
  const axios = useAxiosWithHeader()
  const dispatch = useDispatch()
  const organization = useSelector(Organization.selectors.getOrganization)
  const engineer = useSelector(Engineer.selectors.getEngineer)
  const { setting, repositories } = organization
  const [state, setState] = useState(initialState)
  const [channelsState, setChannelsState] = useState()
  const [teamState, setTeamState] = useState()
  const [repositoryOptions, setRepositoryOptions] = useState([])

  const settingOptions = ['on team', 'on repository']
  const [optionSelection, setOptionSelection] = useState(settingOptions[0])
  const [allRepositories, setAllRepositories] = useState(
    repositories?.map((repo) => repo.name),
  )

  useEffectOnce(() => {
    async function fetchRepo() {
      const { data } = await axios.get(
        `/repositories/find-repo-for-team-channel/${organization.id}`,
      )
      const options = data.map((repo) => repo.name)
      setAllRepositories(options)
      dispatch(Organization.actions.setData({ repositories: data }))
    }

    if (organization.provider === 'github' && !(repositories?.length > 0)) {
      fetchRepo()
      // it means we already have the repositories because we fetched them before a reload of the page
    } else if (organization.provider === 'github') {
      const options = repositories.map((repo) => repo.name)
      setAllRepositories(options)
    }
  })

  const shouldDisableInput = () => {
    const { plan } = organization
    const { channels } = setting
    if (plan === 'trial' || plan === 'business' || plan === 'enterprise') {
      return false
    }
    if (!channels || channels?.length === 0) {
      return false
    }
    const channelEnabled = channels.reduce((channel) => !channel.desactivate)
    if (plan !== 'trial' && plan !== 'business' && channelEnabled) {
      return true
    }
    return false
  }

  const handleTagChange = (values) => {
    setTeamState(values)

    // Check if "all axolo onboarded users" is among the selected values
    const isAllAxoloUserSelected = values.some(
      (value) => value.key === 'All Axolo onboarded users',
    )

    if (isAllAxoloUserSelected) {
      // If "all axolo onboarded users" is selected, keep only that selection
      setTeamState([
        { key: 'All Axolo onboarded users', label: 'All Axolo onboarded users' },
      ])
      return setState({ ...state, teams: ['All Axolo onboarded users'], teamStatus: '' })
    }

    // Proceed as before if "all axolo onboarded users" is not selected
    if (values.length === 0) {
      return setState({ ...state, teams: [], teamStatus: 'warning' })
    }
    const ids = values.map((value) => value.key)
    setState({ ...state, teams: ids, teamStatus: '' })
  }

  const onSave = async () => {
    const isError = handleErrorsInputTeamChannel({
      state,
      setting,
      settingOptions,
      optionSelection,
      repositoryOptions,
    })
    if (isError) return

    setLoading(true)
    const hide = messageInteraction({
      content: 'Creating your channel, setting the topic, and inviting your team..',
      type: 'loading',
      duration: 0,
    })
    const { title, teams, id, existingChannel, notificationTrigger } = state
    try {
      let newSettings = cloneDeep(setting)
      const creator = {
        avatarUrl: engineer.avatarUrl,
        providerLogin: engineer.providerLogin,
        id: engineer.id,
        htmlUrl: engineer.htmlUrl,
      }
      const teamsToInvite = teams
      const channel = {
        title,
        teams: teamsToInvite,
        id,
        existingChannel,
        creator,
        option: optionSelection,
        repositories: repositoryOptions,
        notificationTrigger: notificationTrigger || 'team_membership',
      }
      if (newSettings.channels) {
        newSettings.channels.push(channel)
      } else {
        newSettings.channels = [channel]
      }
      if (setHasCreatedChannel) {
        setHasCreatedChannel(true)
      }
      const updatedSettingsResp = await axios.put(`/settings/${setting._id}`, {
        channels: newSettings.channels,
        updatingTeamChannel: true,
        newChannel: channel,
      })
      setLoading(false)
      if (hide) hide()
      if (updatedSettingsResp.status === 200) {
        if (
          typeof updatedSettingsResp.data === 'string' &&
          updatedSettingsResp.data.includes('Error')
        ) {
          messageInteraction({
            content: updatedSettingsResp.data,
            type: 'error',
          })
        } else {
          messageInteraction({
            content: 'Your channel has been created.',
            type: 'success',
          })
          dispatch(Organization.actions.setData({ setting: updatedSettingsResp.data }))
          setState(initialState)
          // clearing input values for channel and teams
          setChannelsState(null)
          setTeamState(undefined)
          setRepositoryOptions([])
        }
      }
    } catch (error) {
      if (hide) hide()
      console.log('Error creating a team channel', error)
      setLoading(false)
      if (error?.response?.status === 402) {
        messageInteraction({
          content: 'This feature is for Business plan only.',
          type: 'error',
        })
      } else {
        console.log("Couldn't create your channel", error)
        messageInteraction({
          content: "Couldn't create your channel. See logs.",
          type: 'error',
        })
      }
    }
  }

  const channelsOptions = sortBy(
    dedupeByStableId(
      organization?.slackChannels,
      (channel) => channel?.communicationToolId || channel?.id,
      (channel) => channel?.title,
    ).map((c) => {
      const obj = {
        value: c.title,
        label: c.title,
        id: c.communicationToolId || c.id,
      }
      return obj
    }),
    'value',
  )
  useEffectOnce(() => {
    if (preFillAxoloGeneral) {
      const defaultChannelValue = {
        value: '_axolo',
        label: '_axolo',
        id: '_axolo',
      }
      const defaultTeamValue = [allText]
      setChannelsState(defaultChannelValue)
      setTeamState(defaultTeamValue)
      setState({
        ...state,
        title: defaultChannelValue.value,
        existingChannel: false,
        teams: defaultTeamValue,
      })
    }
  })

  const CreateIcon = () => {
    if (onboarding && !disableInput && !(setting?.channels?.length > 0)) {
      return (
        <button
          type="button"
          className="rounded bg-primary px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-hoverPrimary"
          onClick={() => !disableInput && onSave()}
        >
          Create my first team channel
        </button>
      )
    }
    return (
      <PlusCircleIcon
        className={`h-8 w-8 ${
          disableInput ? 'gray-400' : 'text-primary'
        } cursor-pointer hover:text-hoverPrimary`}
        aria-hidden="true"
        style={{ pointerEvents: 'auto' }}
        onClick={() => !disableInput && onSave()}
      />
    )
  }

  const handleTitleChange = async (newValue) => {
    // Prevent selecting the channel ID input option
    if (newValue?.isChannelIdInput) {
      return
    }

    if (newValue?.label?.length > 0 && newValue?.__isNew__) {
      let { label } = newValue

      // Check if this looks like a channel ID or URL (before slugifying)
      const originalLabel = label
      const parsedChannel = parseChannelInput(originalLabel)

      if (parsedChannel) {
        // This looks like a channel ID or URL, try to fetch channel info
        const response = await fetchChannelById(parsedChannel.channelId)
        if (response?.data) {
          // Add to both filtered options and base channels options
          const channelOption = {
            value: response.data.name,
            label: response.data.name,
            id: response.data.id,
          }

          // Update filtered options
          setFilteredOptions((prevOptions) => {
            const cleanOptions = prevOptions.filter(
              (opt) => !opt.isChannelIdInput && opt.value !== channelOption.value,
            )
            return [channelOption, ...cleanOptions]
          })

          // Clear the input first, then set the new value
          setChannelsState(null)
          setState(initialState)

          // Use setTimeout to ensure the clear happens first
          setTimeout(() => {
            setChannelsState(channelOption)
            setState({
              ...state,
              title: channelOption.value,
              id: channelOption.id,
              existingChannel: true,
            })
          }, 10)

          return
        }
      }

      // Normal slugify process for channel names
      label = slugify(label, { replacement: '_', trim: false })
      label = label?.replace(/[^\w\s-]/g, '_').toLowerCase()
      newValue.label = label
      newValue.value = label
    }
    setChannelsState(newValue)

    if (!(newValue?.label?.length > 0)) return
    const { value = '', id = '' } = newValue
    if (newValue?.id?.length > 0) {
      setState({ ...state, title: value, id, existingChannel: true })
    } else if (newValue) {
      setState({ ...state, title: value, existingChannel: false })
    } else {
      setState({ ...state, title: '', titleStatus: '' })
    }
  }
  const formatLabel = (inputValue) => {
    // Check if this looks like a channel ID or URL
    const parsedChannel = parseChannelInput(inputValue)

    if (parsedChannel) {
      if (parsedChannel.isUrl) {
        return `Use ${parsedChannel.channelId} as a team channel`
      } else {
        return `Use ${inputValue} as a team channel`
      }
    }

    return `Create new channel "${inputValue}"`
  }

  const disableInput = shouldDisableInput() || loading

  // limit to 10 channels showing to avoid performance issues with Enterprise
  const maxChannelsToShow = 10
  const getInitialOptions = () => {
    const initial = channelsOptions.slice(0, maxChannelsToShow)
    if (initial.length <= 5) {
      initial.push({
        value: '__CHANNEL_ID_INPUT__',
        label: '__CHANNEL_ID_INPUT__',
        isChannelIdInput: true,
      })
    }
    return initial
  }
  const [filteredOptions, setFilteredOptions] = useState(getInitialOptions())
  const filterAndLimitOptions = (inputValue) => {
    const filtered = channelsOptions
      .filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, maxChannelsToShow)

    // Add custom channel ID input option if there are 5 or fewer results
    if (filtered.length <= 5) {
      filtered.push({
        value: '__CHANNEL_ID_INPUT__',
        label: '__CHANNEL_ID_INPUT__',
        isChannelIdInput: true,
      })
    }

    return filtered
  }
  // end of limiting code for channels

  function changeChannelOption(value) {
    setOptionSelection(value)
    if (value === 'on team') {
      setRepositoryOptions([])
    } else {
      setTeamState([])
    }
  }

  const parseChannelInput = (input) => {
    const trimmedInput = input.trim()

    // Check if it's a Slack URL (https://workspace.slack.com/archives/CHANNELID)
    const urlMatch = trimmedInput.match(
      /https:\/\/[^\/]+\.slack\.com\/archives\/([A-Z0-9]+)/i,
    )
    if (urlMatch) {
      return {
        channelId: urlMatch[1],
        isUrl: true,
        original: trimmedInput,
      }
    }

    // Check if it's a direct channel ID
    if (/^[A-Z0-9]{9,11}$/i.test(trimmedInput)) {
      return {
        channelId: trimmedInput,
        isUrl: false,
        original: trimmedInput,
      }
    }

    return null
  }

  const fetchChannelById = async (channelId) => {
    try {
      const { data } = await axios.post(
        `/conversations/channel/info`,
        {
          channelId,
          organizationId: organization.id,
        },
        {
          // Add a flag to prevent global error handling/logout on 401
          skipAuthRedirect: true,
          validateStatus: (status) => status < 500, // Don't throw for 4xx errors
        },
      )

      console.log('data', data)

      // Check if we got a successful response
      if (data && !data.error) {
        return data
      }

      return null
    } catch (error) {
      console.log('Error fetching channel info', error)

      // Handle 401 errors gracefully without triggering logout
      if (error?.response?.status === 401) {
        console.log('Channel not found or access denied for channel ID:', channelId)
        return null
      }

      // For other errors, also return null but don't trigger global error handling
      return null
    }
  }

  // Custom Option component for react-select
  const CustomOption = (props) => {
    const { data, innerRef, innerProps } = props

    if (data.isChannelIdInput) {
      return (
        <div ref={innerRef} {...innerProps}>
          <ChannelIdInputOption />
        </div>
      )
    }

    // Default option rendering
    return <selectComponents.Option {...props} />
  }

  const customComponents = {
    Option: CustomOption,
  }

  return (
    <tr key="newTeamChannelLine">
      <td className={classChannelColumn}>
        <div className="flex text-xs">
          <CreatableSelect
            createOptionPosition="first"
            isClearable
            components={customComponents}
            onInputChange={(newValue) => {
              // Check if the input looks like a channel ID or URL before processing
              const parsedChannel = parseChannelInput(newValue)

              if (parsedChannel) {
                // If it's a channel ID or URL, just update the filtered options
                // Don't immediately call handleTitleChange - let the user select the option
                setFilteredOptions(filterAndLimitOptions(newValue))
                return
              }

              let inputValue = slugify(newValue, { replacement: '_', trim: false })
              inputValue = inputValue?.replace(/[^\w\s-]/g, '_').toLowerCase()
              setFilteredOptions(filterAndLimitOptions(inputValue))
              // if there is a text in the input, we save it as an option
              // but if we select an existing channel, the event will trigger without anything in it
              // so it will be the onChange props which will select the option
              if (inputValue?.length > 0) {
                handleTitleChange({
                  label: inputValue,
                  value: inputValue,
                  __isNew__: true,
                })
              }
            }}
            onChange={handleTitleChange}
            onBlur={(e) => {
              const inputValue = e.target.value
              if (inputValue) {
                const parsedChannel = parseChannelInput(inputValue)
                if (parsedChannel) {
                  // Automatically process channel ID/URL when user clicks outside
                  handleTitleChange({
                    label: inputValue,
                    value: inputValue,
                    __isNew__: true,
                  })
                }
              }
            }}
            openMenuOnClick={false}
            options={filteredOptions}
            formatCreateLabel={formatLabel}
            value={channelsState}
            placeholder="Start typing or paste a channel ID"
            className="w-[240px]"
            noOptionsMessage={({ inputValue }) =>
              inputValue ? `Create a new channel "${inputValue}"` : 'Create a new channel'
            }
          />
          <RefreshConversationsButton />
        </div>
      </td>
      {organization?.provider === 'github' && (
        <td className={classOptionTeamChannel}>
          <div className="text-xs text-gray-600">
            Channel is based
            <Select
              value={optionSelection}
              onChange={(value) => changeChannelOption(value)}
              options={settingOptions.map((option) => ({ value: option, label: option }))}
              size="small"
              style={{ width: '100px', paddingLeft: '3px', fontSize: '11px' }}
            />
          </div>
        </td>
      )}
      <td className={classTeamColumn}>
        <div className="flex w-full flex-col gap-1">
          {organization?.provider === 'github' && (
            <div className="flex w-full items-center gap-1">
              <Select
                value={
                  optionSelection === 'on team' ? 'all repositories' : repositoryOptions
                }
                mode="multiple"
                onChange={(value) => setRepositoryOptions(value)}
                options={allRepositories.map((option) => ({
                  value: option,
                  label: option,
                }))}
                style={{ width: '100%', minWidth: '120px', fontSize: '11px' }}
                placeholder="Select repo"
                disabled={optionSelection === 'on team'}
                size="small"
              />
            </div>
          )}

          <div className="flex w-full items-center gap-1">
            <Select
              status={state.teamStatus}
              mode="multiple"
              allowClear
              style={{ width: '100%', minWidth: '120px', fontSize: '11px' }}
              placeholder="Select teams"
              labelInValue
              value={teamState}
              onChange={(e) => handleTagChange(e)}
              size="small"
            >
              {children}
            </Select>
            <RefreshTeamButton />
          </div>
        </div>
      </td>
      {organization?.provider === 'github' && (
        <td className="px-1 py-2">
          <Select
            value={state.notificationTrigger || 'team_membership'}
            onChange={(value) => setState({ ...state, notificationTrigger: value })}
            disabled={disableInput}
            size="small"
            style={{ width: '180px', fontSize: '11px' }}
            options={[
              { value: 'team_membership', label: 'Member creates PR' },
              { value: 'team_assignment', label: 'Team assigned' },
            ]}
          />
        </td>
      )}
      <td className={classCreatorColumn}>
        <div className="flex flex-col items-start">
          <UserAvatar record={engineer} size="small" />
          <p className="mb-0 mt-1 w-full truncate text-xs text-gray-700">
            {engineer?.providerLogin}
          </p>
        </div>
      </td>

      <td className={classActionColumn}>
        <span href="#">
          <div className="flex  justify-center">
            {disableInput && organization?.plan !== 'trial' ? (
              <Tooltip
                title="Upgrade to the business plan to access this functionality"
                placement="top"
                trigger={['hover']}
              >
                <div>
                  <PlusCircleIcon
                    className={`h-8 w-8 ${
                      disableInput ? 'gray-400' : 'text-primary'
                    } cursor-pointer hover:text-hoverPrimary`}
                    aria-hidden="true"
                    style={{ pointerEvents: 'auto' }}
                    onClick={() => !disableInput && onSave()}
                  />
                </div>
              </Tooltip>
            ) : (
              <CreateIcon />
            )}
          </div>
        </span>
      </td>
    </tr>
  )
}
